import { Link, useNavigate } from 'react-router-dom'
import { SecurityAvatar } from '@/securities/securityAvatar.tsx'
import { APP_ROUTES } from '@/common/constants'
import PrecisionAwarePriceLabel from '@/common/models/precisionAwarePriceLabel'
import { Button } from '@/common/ui/button'
import EtfCardSelectedAnalytics from '@/common/analytics/events/findStocks/EtfCardSelectedAnalytics'
import { PriceDiff } from '@/common/ui/PriceDiff'
import { FIND_STOCKS_COPY } from '@/locales/en'
import { useOrderSource } from '@/common/context/useOrderSource'
import { ORDER_ENTRY_POINT, ORDER_FROM } from '@/common/context/orderSourceValues'

export function EtfCard({
    securityCode,
    securityName,
    description,
    price,
    changeInPriceYTD,
    positiveThemes,
    avoidedThemes,
    loading,
}: {
    securityCode: string
    securityName: string
    description: string
    price: number
    changeInPriceYTD: number
    positiveThemes?: any[]
    avoidedThemes?: any[]
    loading?: boolean
}) {
    const navigate = useNavigate()
    const { setOrderSource } = useOrderSource()
    if (loading) return null

    const posCount = positiveThemes?.length ?? 0
    const negCount = avoidedThemes?.length ?? 0

    const trackAnalytics = () => {
        setOrderSource(ORDER_FROM.FIND_STOCKS, ORDER_ENTRY_POINT.ETF_CARD)
        EtfCardSelectedAnalytics.track({
            security_code: securityCode,
            change_in_price_ytd: changeInPriceYTD,
            positive_screens: posCount,
            negative_screens: negCount,
        })
    }

    return (
        <div className="relative self-stretch w-full p-6 bg-white rounded-[10px] outline outline-1 outline-offset-[-0.50px] outline-neutral-300 inline-flex flex-col md:flex-row justify-start items-start gap-6">
            <Link
                to={`${APP_ROUTES.SECURITY}/${securityCode}`}
                aria-label={`View ${securityCode}`}
                className="absolute inset-0 z-10 md:hidden pointer-events-auto"
                onClick={trackAnalytics}
            />
            <div className="flex-1 inline-flex flex-col justify-start items-start gap-4">
                <div className="self-stretch inline-flex justify-start items-start gap-4">
                    <div className="w-10 h-10 flex justify-start items-start gap-2.5">
                        <Link to={`${APP_ROUTES.SECURITY}/${securityCode}`} onClick={trackAnalytics}>
                            <SecurityAvatar height={10} width={10} securityCode={securityCode} />
                        </Link>
                    </div>
                    <div className="flex-1 inline-flex flex-col justify-center items-start gap-4">
                        <div className="self-stretch h-11 flex flex-col justify-start items-start gap-0.5">
                            <div className="self-stretch bg-white/0 rounded-md inline-flex justify-start items-start gap-2">
                                <div className="flex-1 justify-start text-black text-base font-medium font-['Inter'] leading-6 line-clamp-1">
                                    {securityCode}
                                </div>
                            </div>
                            <div className="self-stretch justify-start text-content-secondary text-sm font-normal font-['Inter'] leading-5 line-clamp-1">
                                {securityName}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="inline-flex justify-start items-start gap-2">
                    <div className="px-3 py-1 bg-brand-accent-soft-lime rounded-2xl backdrop-blur-[5px] flex justify-start items-center gap-1 overflow-hidden">
                        <div className="justify-center text-brand-primary-forest text-xs font-normal font-['Inter'] leading-4">
                            {FIND_STOCKS_COPY.etfCard.positiveScreens.replace('{count}', posCount.toString())}
                        </div>
                    </div>
                    <div className="px-3 py-1 bg-primary-dust rounded-2xl backdrop-blur-[5px] flex justify-start items-center gap-1 overflow-hidden">
                        <div className="justify-center text-brand-primary-forest text-xs font-normal font-['Inter'] leading-4">
                            {FIND_STOCKS_COPY.etfCard.negativeScreens.replace('{count}', negCount.toString())}
                        </div>
                    </div>
                </div>

                <div className="hidden md:block self-stretch h-16 justify-start text-black text-sm font-normal font-['Inter'] leading-5 line-clamp-3">
                    {description}
                </div>
            </div>

            <div className="w-full md:w-auto inline-flex flex-col justify-start items-end gap-6">
                <div className="self-stretch flex flex-col justify-center items-end gap-2">
                    <div className="flex flex-col justify-start items-end gap-1">
                        <div className="justify-start text-content-secondary text-sm font-normal font-['Inter'] leading-5">
                            {FIND_STOCKS_COPY.etfCard.yearToDateChange}
                        </div>
                        <div className="inline-flex justify-start items-center gap-1">
                            <div className="justify-start text-brand-grass text-base font-medium font-['Inter'] leading-6 line-clamp-1">
                                {!isNaN(changeInPriceYTD) && (
                                    <PriceDiff value={changeInPriceYTD} label={`${Math.abs(changeInPriceYTD)}%`} />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="justify-start text-black text-sm font-medium font-['Inter'] leading-5">{`${PrecisionAwarePriceLabel.of(price)}`}</div>
                </div>

                <Button
                    className="hidden md:block"
                    variant="tertiary"
                    onClick={() => {
                        trackAnalytics()
                        navigate(`${APP_ROUTES.SECURITY}/${securityCode}`)
                    }}
                >
                    <div className="justify-start text-content-primary text-sm font-medium font-['Inter'] leading-5">
                        {FIND_STOCKS_COPY.etfCard.viewStock}
                    </div>
                </Button>
            </div>
        </div>
    )
}

export default EtfCard
